import React, { useEffect, useRef, useState } from 'react';
import { Pencil } from 'lucide-react';
import { useLocale } from '../contexts/LocaleContext';
import { useToast } from '../contexts/ToastContext';
import { cn } from '../lib/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from './ui/tooltip';
import { ConfirmDialog } from './ConfirmDialog';

type DisplayElement = 'span' | 'h2' | 'h3';

export type InlineEditableCommitResult = void | boolean;

interface InlineEditableTextProps {
  value: string;
  onChange?: (value: string) => void;
  onCommit?: (value: string) => InlineEditableCommitResult | Promise<InlineEditableCommitResult>;
  savedFieldLabel?: string;
  editing?: boolean;
  onEditingChange?: (editing: boolean) => void;
  disabled?: boolean;
  readOnly?: boolean;
  placeholder?: string;
  tooltip?: React.ReactNode;
  className?: string;
  displayClassName?: string;
  inputClassName?: string;
  as?: DisplayElement;
  selectOnFocus?: boolean;
  skipDialogAutofocus?: boolean;
  stopClickPropagation?: boolean;
  allowEmpty?: boolean;
  multiline?: boolean;
  rows?: number;
}

const displayBaseClassName =
  'group inline-flex min-w-0 max-w-full items-center gap-1 rounded border border-transparent px-1 py-0.5 -mx-1 transition-[border-color,background-color,color] duration-150';

const editableDisplayClassName =
  'cursor-text hover:border-muted-foreground hover:bg-muted/40 focus-visible:outline-none focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/30';

const inputBaseClassName =
  'min-w-0 w-full rounded border border-border bg-background px-2 py-1 text-foreground outline-none ring-offset-background focus-visible:ring-2 focus-visible:ring-ring/30 disabled:cursor-not-allowed disabled:opacity-50';

const getModifierLabel = (): string => {
  if (typeof navigator === 'undefined') {
    return 'Ctrl';
  }
  return /Mac|iPhone|iPad/.test(navigator.platform) ? '⌘' : 'Ctrl';
};

export const InlineEditableText: React.FC<InlineEditableTextProps> = ({
  value,
  onChange,
  onCommit,
  savedFieldLabel,
  editing: editingProp,
  onEditingChange,
  disabled = false,
  readOnly = false,
  placeholder,
  tooltip,
  className,
  displayClassName,
  inputClassName,
  as: DisplayTag = 'span',
  selectOnFocus = true,
  skipDialogAutofocus = false,
  stopClickPropagation = false,
  allowEmpty = false,
  multiline = false,
  rows = 3,
}) => {
  const { t } = useLocale();
  const { showFieldSavedToast } = useToast();
  const isEditingControlled = editingProp !== undefined;
  const [uncontrolledEditing, setUncontrolledEditing] = useState(false);
  const editing = isEditingControlled ? editingProp : uncontrolledEditing;
  const [draft, setDraft] = useState(value);
  const [editMinHeight, setEditMinHeight] = useState<number | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const valueAtEditStartRef = useRef(value);
  const ignoreBlurRef = useRef(false);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
  const displayRef = useRef<HTMLElement>(null);
  const isCancellingRef = useRef(false);
  const draftRef = useRef(value);
  const editMinHeightRef = useRef<number | null>(null);
  const pendingBlurValueRef = useRef<string | null>(null);
  const skipCancelOnConfirmCloseRef = useRef(false);
  const isEmpty = !value.trim();
  const canEdit = !readOnly && !disabled;

  const setEditing = (next: boolean): void => {
    if (!isEditingControlled) {
      setUncontrolledEditing(next);
    }
    onEditingChange?.(next);
  };

  const syncMultilineTextareaHeight = (): void => {
    const el = inputRef.current;
    if (!el || !(el instanceof HTMLTextAreaElement)) {
      return;
    }
    const minHeight = editMinHeightRef.current ?? 0;
    el.style.height = 'auto';
    el.style.height = `${Math.max(el.scrollHeight, minHeight)}px`;
  };

  useEffect(() => {
    if (!editing) {
      setDraft(value);
      draftRef.current = value;
    }
  }, [value, editing]);

  useEffect(() => {
    if (!editing) {
      ignoreBlurRef.current = false;
      return;
    }
    ignoreBlurRef.current = true;
    const timeoutId = window.setTimeout(() => {
      ignoreBlurRef.current = false;
    }, 0);
    return () => window.clearTimeout(timeoutId);
  }, [editing]);

  useEffect(() => {
    if (!editing || !inputRef.current) return;
    if (confirmOpen) return;
    inputRef.current.focus();
    if (selectOnFocus) {
      inputRef.current.select();
    }
    if (multiline) {
      syncMultilineTextareaHeight();
    }
  }, [editing, selectOnFocus, multiline, confirmOpen]);

  useEffect(() => {
    if (!editing || !multiline) {
      return;
    }
    syncMultilineTextareaHeight();
  }, [draft, editing, multiline]);

  const updateDraft = (next: string): void => {
    draftRef.current = next;
    setDraft(next);
    onChange?.(next);
  };

  const clearEditMinHeight = (): void => {
    editMinHeightRef.current = null;
    setEditMinHeight(null);
  };

  const startEditing = (event?: React.SyntheticEvent): void => {
    if (!canEdit) return;
    if (stopClickPropagation && event) {
      event.stopPropagation();
    }
    valueAtEditStartRef.current = value;
    draftRef.current = value;
    setDraft(value);
    setConfirmOpen(false);
    pendingBlurValueRef.current = null;
    if (multiline && displayRef.current) {
      const height = Math.ceil(displayRef.current.getBoundingClientRect().height);
      editMinHeightRef.current = height;
      setEditMinHeight(height);
    } else {
      clearEditMinHeight();
    }
    setEditing(true);
  };

  const cancelEditing = (): void => {
    isCancellingRef.current = true;
    pendingBlurValueRef.current = null;
    setConfirmOpen(false);
    updateDraft(valueAtEditStartRef.current);
    setEditing(false);
    clearEditMinHeight();
  };

  const commitEditing = (rawValue?: string): void => {
    if (isCancellingRef.current) {
      isCancellingRef.current = false;
      return;
    }
    const sourceValue = rawValue ?? inputRef.current?.value ?? draftRef.current;
    const trimmed = sourceValue.trim();
    if (!trimmed && !allowEmpty) {
      updateDraft(valueAtEditStartRef.current);
      pendingBlurValueRef.current = null;
      setConfirmOpen(false);
      setEditing(false);
      clearEditMinHeight();
      return;
    }
    const nextValue = allowEmpty ? sourceValue.trim() : trimmed;
    const valueChanged = nextValue !== valueAtEditStartRef.current.trim();
    if (onCommit && valueChanged) {
      void (async () => {
        try {
          const result = await onCommit(nextValue);
          if (savedFieldLabel && result !== false) {
            showFieldSavedToast(savedFieldLabel);
          }
        } catch {
        }
      })();
    } else if (!onCommit && onChange) {
      onChange(nextValue);
    }
    draftRef.current = nextValue;
    setDraft(nextValue);
    pendingBlurValueRef.current = null;
    setConfirmOpen(false);
    setEditing(false);
    clearEditMinHeight();
  };

  const resolveOutsideClickValue = (sourceValue: string): string | null => {
    const trimmed = sourceValue.trim();
    if (!trimmed && !allowEmpty) {
      return null;
    }
    return trimmed;
  };

  const handleOutsideClick = (sourceValue: string): void => {
    if (confirmOpen) {
      return;
    }
    const nextValue = resolveOutsideClickValue(sourceValue);
    if (nextValue === null) {
      cancelEditing();
      return;
    }
    const valueChanged = nextValue !== valueAtEditStartRef.current.trim();
    if (!valueChanged) {
      pendingBlurValueRef.current = null;
      setConfirmOpen(false);
      setEditing(false);
      clearEditMinHeight();
      return;
    }
    pendingBlurValueRef.current = nextValue;
    setConfirmOpen(true);
  };

  const handleConfirmOpenChange = (open: boolean): void => {
    if (open) {
      setConfirmOpen(true);
      return;
    }
    setConfirmOpen(false);
    if (skipCancelOnConfirmCloseRef.current) {
      skipCancelOnConfirmCloseRef.current = false;
      return;
    }
    cancelEditing();
  };

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
  ): void => {
    if (stopClickPropagation) {
      event.stopPropagation();
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      cancelEditing();
      return;
    }
    if (event.key === 'Enter') {
      const isModEnter = event.metaKey || event.ctrlKey;
      if (multiline && !isModEnter) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      commitEditing(event.currentTarget.value);
    }
  };

  const outsideClickHint = multiline
    ? t('inline_editable_outside_click_hint_multiline').replace('{mod}', getModifierLabel())
    : t('inline_editable_outside_click_hint_single');

  const confirmDialog = (
    <ConfirmDialog
      open={confirmOpen}
      onOpenChange={handleConfirmOpenChange}
      title={t('inline_editable_apply_or_cancel')}
      description={outsideClickHint}
      confirmLabel={t('apply')}
      cancelLabel={t('cancel')}
      destructive={false}
      onConfirm={() => {
        skipCancelOnConfirmCloseRef.current = true;
        commitEditing(pendingBlurValueRef.current ?? draftRef.current);
      }}
    />
  );

  if (editing) {
    const fieldClassName = cn(
      inputBaseClassName,
      multiline ? 'resize-y overflow-hidden whitespace-pre-wrap' : null,
      className,
      inputClassName,
    );
    const fieldProps = {
      value: draft,
      onChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        updateDraft(event.target.value);
      },
      onBlur: (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (ignoreBlurRef.current) return;
        handleOutsideClick(event.currentTarget.value);
      },
      onKeyDown: handleKeyDown,
      onClick: (event: React.MouseEvent) => {
        if (stopClickPropagation) {
          event.stopPropagation();
        }
      },
      disabled,
      placeholder,
      autoFocus: true as const,
      'data-dialog-skip-autofocus': skipDialogAutofocus ? '' : undefined,
      'data-dialog-primary-ignore-shortcut': '',
      className: fieldClassName,
    };

    if (multiline) {
      return (
        <>
          <textarea
            {...fieldProps}
            ref={inputRef as React.RefObject<HTMLTextAreaElement>}
            rows={rows}
            style={editMinHeight ? { minHeight: `${editMinHeight}px` } : undefined}
          />
          {confirmDialog}
        </>
      );
    }

    return (
      <>
        <input
          {...fieldProps}
          ref={inputRef as React.RefObject<HTMLInputElement>}
          type="text"
        />
        {confirmDialog}
      </>
    );
  }

  const displayText = value || placeholder || '';
  const editLabel = multiline
    ? t('inline_editable_click_to_edit')
    : t('inline_editable_click_to_rename');
  const resolvedTooltip =
    tooltip ?? (canEdit ? editLabel : value || undefined);

  const display = (
    <DisplayTag
      ref={displayRef as React.RefObject<never>}
      className={cn(
        displayBaseClassName,
        multiline ? 'items-start' : null,
        canEdit ? editableDisplayClassName : 'cursor-default',
        className,
        displayClassName,
      )}
      onMouseDown={(event) => {
        if (!canEdit) return;
        event.preventDefault();
        startEditing(event);
      }}
      onClick={startEditing}
      role={canEdit ? 'button' : undefined}
      tabIndex={canEdit ? 0 : undefined}
      aria-label={canEdit ? editLabel : undefined}
      data-dialog-skip-autofocus={skipDialogAutofocus ? '' : undefined}
      onKeyDown={(event) => {
        if (!canEdit) return;
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          startEditing(event);
        }
      }}
    >
      <span
        className={cn(
          'min-w-0',
          multiline ? 'whitespace-pre-wrap break-words' : 'truncate',
          isEmpty && placeholder ? 'italic text-muted-foreground' : null,
        )}
      >
        {displayText}
      </span>
      {canEdit && (
        <Pencil
          aria-hidden
          className="!h-3.5 !w-3.5 shrink-0 text-muted-foreground opacity-0 transition-opacity duration-150 group-hover:opacity-100 group-focus-visible:opacity-100"
        />
      )}
    </DisplayTag>
  );

  if (!resolvedTooltip) {
    return display;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>{display}</TooltipTrigger>
      <TooltipContent side="bottom" align="start" className="max-w-[min(24rem,calc(100vw-2rem))] break-words">
        {typeof resolvedTooltip === 'string' ? <p>{resolvedTooltip}</p> : resolvedTooltip}
      </TooltipContent>
    </Tooltip>
  );
};
